import React, { useRef, useState } from "react";
import { Box } from "@material-ui/core";
import { useHistory } from "react-router";
import {
  CreateRotationSchemeServices,
  UpdateRotationScheme,
} from "../../../../Services";
import { CustomButton, CustomDialog, PageTitle } from "../../../../Components";
import AssignAgent from "../AssignAgent/AssignAgent";
import RotationCriteriaSummary from "../RotationCriteriaSummary/RotationCriteriaSummary";
import {
  AddRotationStepper,
  AddNewRotationForm,
  SchemaSettingsForm,
} from "../";
import { useVerticalNav } from "../../../../Contexts/VerticalNavContext";
import { buildQueryBody } from "../util";
import { useSelectedTheme, useTranslate } from "../../../../Hooks";

import useStyles from "../styles";

import { CheckCircleOutlined, PlusIcon } from "../../../../assets/icons";

function NewAddAgentsRotationCriteriaView() {
  const [activeStep, setActiveStep] = useState(0);
  const [formValues, setFormValues] = useState({
    addRotationForm: {},
    schemaSettingsForm: {},
  });
  const { translate } = useTranslate("NewAgentsRotationCriteria");
  const {
    theme: { palette },
  } = useSelectedTheme();
  const [activeFields, setActiveFields] = useState(null);
  const [rotationSchemaId, setRotationSchemaId] = useState(null);
  const { setAlertBoxContent } = useVerticalNav();
  const styles = useStyles();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAssignAgentModalOpen, setIsAssignAgentModalOpen] = useState(false);
  const addRotationFormikRef = useRef();
  const schemaSettingsFormikRef = useRef();
  const navigate = useHistory();

  const pageSubtitle = [
    translate("add-agent-rotation-page-subtitle"),
    translate("schema-settings-subtitle"),
    translate("assign-agent-subtitle"),
    translate("schema-summary-title"),
  ];

  const saveSchema = async () => {
    const body = buildQueryBody(formValues);
    let res = null;
    // if schemaId is present, that means the schema was already created and we are in edit mode
    if (rotationSchemaId) {
      res = await UpdateRotationScheme(rotationSchemaId, body);
    } else {
      res = await CreateRotationSchemeServices(body);
      setRotationSchemaId(res);
    }
    if (!(res && res.status && res.status !== 200)) {
      setAlertBoxContent({
        display: true,
        variant: "success",
        title: translate(`create-schema-modal-title`),
        onClose: () => {
          setAlertBoxContent(null);
        },
      });
      setActiveStep(activeStep + 1);
    } else {
      const errorMessage = (res && res.data && res.data.Message).split(": ")[1];
      if (errorMessage === "___DUPLICATE_ROTATION_SCHEME_ORDER___")
        setAlertBoxContent({
          display: true,
          variant: "error",
          title: "Failed to Create Schema.",
          onClose: () => {
            setAlertBoxContent(null);
          },
        });
      else
        setAlertBoxContent({
          display: true,
          variant: "error",
          title: translate("Shared:Server-Error"),
          onClose: () => {
            setAlertBoxContent(null);
          },
        });
    }
  };

  const saveActiveFieldSelection = (fields) => {
    setActiveFields({ ...fields });
  };

  const stepperSchema = {
    steps: [
      {
        stepLabel: translate("rotation-criteria"),
        actionButtons: [
          {
            labelText: translate("next"),
            onClickHandler: (data) => {
              addRotationFormikRef.current.validateForm().then((errors) => {
                const isValid = Object.keys(errors).length === 0;
                if (isValid) {
                  setFormValues({
                    ...formValues,
                    addRotationForm: addRotationFormikRef.current.values,
                  });
                  saveActiveFieldSelection(data);
                  setActiveStep(activeStep + 1);
                }
                const touchedFields = Object.keys(errors).reduce((acc, key) => {
                  acc[key] = true;
                  return acc;
                }, {});

                addRotationFormikRef.current.setTouched(touchedFields, true);
                addRotationFormikRef.current.setErrors(errors);
              });
            },
            type: "primary",
            isShown: true,
          },
          {
            labelText: translate("cancel"),
            onClickHandler: () => {
              navigate.replace("/home/rotation-criteria/view");
            },
            type: "secondary",
            isShown: true,
          },
        ],
      },
      {
        stepLabel: translate("schema-setting"),
        actionButtons: [
          {
            labelText: rotationSchemaId
              ? translate("save")
              : translate("create-continue"),
            onClickHandler: () => {
              setFormValues({
                ...formValues,
                schemaSettingsForm: {
                  ...schemaSettingsFormikRef.current.values,
                },
              });
              if (!rotationSchemaId) return setIsModalOpen(true);
              saveSchema();
            },
            type: "primary",
            isShown: true,
          },

          {
            labelText: translate("back"),
            onClickHandler: () => {
              setFormValues({
                ...formValues,
                schemaSettingsForm: {
                  ...schemaSettingsFormikRef.current.values,
                },
              });
              if (activeStep === 0) return;
              setActiveStep(activeStep - 1);
            },
            type: "secondary",
            isShown: true,
          },
          {
            labelText: translate("cancel"),
            onClickHandler: () => {
              navigate.replace("/home/rotation-criteria/view");
            },
            type: "secondary",
            isShown: true,
          },
        ],
      },

      {
        stepLabel: translate("assign-agent"),
        actionButtons: [
          {
            labelText: translate("confirm"),
            onClickHandler: () => {
              if (activeStep + 1 >= stepperSchema.steps.length) return;
              setActiveStep(activeStep + 1);
            },
            type: "primary",
            isShown: true,
          },

          {
            labelText: translate("modify-settings"),
            onClickHandler: () => {
              if (activeStep === 0) return;
              setActiveStep(0);
            },
            type: "secondary",
            isShown: true,
          },
          {
            labelText: translate("cancel"),
            onClickHandler: () => {
              navigate.replace("/home/rotation-criteria/view");
            },
            type: "secondary",
            isShown: true,
          },
        ],
      },
      {
        stepLabel: translate("rotation-summary"),
        actionButtons: [
          {
            labelText: translate("finish"),
            onClickHandler: () => {
              navigate.replace("/home/rotation-criteria/view");
            },
            type: "primary",
            isShown: true,
          },

          {
            labelText: translate("back"),
            onClickHandler: () => {
              setActiveStep(activeStep - 1);
            },
            type: "secondary",
            isShown: true,
          },
        ],
      },
    ],
  };

  const generateStepperActions = (data) => {
    // generate the action buttons related to current active step
    return stepperSchema.steps[activeStep].actionButtons.map(
      ({ isShown, type, labelText, onClickHandler }, index) => {
        return isShown ? (
          <CustomButton
            key={`${stepperSchema.steps[activeStep].stepLabel}_${labelText}`}
            boxShadow="xs"
            size="md"
            variant={type === "secondary" ? "outlined" : ""}
            color={type}
            id={`${type}_${labelText}`}
            onClick={() => onClickHandler(data)}
          >
            {labelText}
          </CustomButton>
        ) : null;
      }
    );
  };

  return (
    <>
      <Box
        className={styles.headingContainer}
        display={"flex"}
        alignItems={"flex-start"}
      >
        <PageTitle
          title={translate("add-agent-rotation-page-title")}
          subTitle={pageSubtitle[activeStep]}
          wrapperClasses={styles.pageTitle}
          hideDivider
        />
        {activeStep === 2 && (
          <CustomButton
            boxShadow="xs"
            size="lg"
            variant="primary"
            onClick={() => {
              setIsAssignAgentModalOpen(true);
            }}
            startIcon={
              <PlusIcon
                width="20"
                height="20"
                fill={palette.button.primary_fg}
              />
            }
          >
            {translate("assign-new-agent")}
          </CustomButton>
        )}
      </Box>
      <AddRotationStepper
        activeStep={activeStep}
        stepperSchema={stepperSchema}
      />
      {activeStep === 0 && (
        <AddNewRotationForm
          generateStepperActions={generateStepperActions}
          formikRef={addRotationFormikRef}
          formValues={formValues.addRotationForm}
          activeOptionalFields={activeFields}
        />
      )}
      {activeStep === 1 && (
        <SchemaSettingsForm
          formikRef={schemaSettingsFormikRef}
          generateStepperActions={generateStepperActions}
          formValues={formValues.schemaSettingsForm}
        />
      )}
      {activeStep === 2 && (
        <AssignAgent
          formikRef={schemaSettingsFormikRef}
          generateStepperActions={generateStepperActions}
          formValues={formValues.schemaSettingsForm}
          isAssignAgentModalOpen={isAssignAgentModalOpen}
          setIsAssignAgentModalOpen={setIsAssignAgentModalOpen}
          rotationSchemaId={rotationSchemaId}
        />
      )}
      {activeStep === 3 && (
        <RotationCriteriaSummary
          generateStepperActions={generateStepperActions}
          schemaValues={{
            ...formValues.schemaSettingsForm,
            ...formValues.addRotationForm,
          }}
        />
      )}
      <CustomDialog
        open={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
        }}
        hideActionsBorder
        onConfirm={() => {
          saveSchema();
          setIsModalOpen(false);
        }}
        title={translate("create-schema-modal-title")}
        subtitle={translate("create-schema-modal-sub-text")}
        cancelText={translate("cancel")}
        confirmText={translate("confirm")}
        width="544px"
        headingIcon={
          <div className={styles.modalIcon}>
            <CheckCircleOutlined width="20" height="20" fill={"#079455"} />
          </div>
        }
      />
    </>
  );
}

export default NewAddAgentsRotationCriteriaView;
